/* Shared Klaviyo signup for any <form data-sb-klaviyo>. Same client API, revision and payload
   as the inline script in sections/sb-footer.liquid (company R3TYpW, list VxsjcM), written once
   here so new signup forms do not paste another copy. sb-footer still carries its own inline
   copy; moving it onto this file is a separate, footer-only change.

   Form contract:
     data-company, data-list            Klaviyo public key and list ID
     data-loading-label                  button text while sending
     data-invalid-message, data-consent-message, data-fail-message
     input[type=email], optional input[name=consent] (required when present)
     [data-sb-klaviyo-fields]            hidden after success
     [data-sb-klaviyo-error], [data-sb-klaviyo-success]  message elements (start hidden)
     button[type=submit]                 render it `disabled`: this script enables it once the
                                         handler is attached, so without JavaScript the form
                                         cannot submit natively (which would put the email in the
                                         URL and subscribe nobody).
   Forms are (re)bound on `shopify:section:load`, because the theme editor re-renders a section
   without re-running its scripts. Binding is idempotent per form. */
(() => {
  if (window.sbKlaviyoSubscribeLoaded) return;
  window.sbKlaviyoSubscribeLoaded = true;

  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function subscribe(company, list, email) {
    return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(company)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: { profile: { data: { type: 'profile', attributes: { email } } } },
          relationships: { list: { data: { type: 'list', id: list } } },
        },
      }),
    });
  }

  function bind(form) {
    if (form.dataset.sbKlaviyoReady) return;
    const email = form.querySelector('input[type="email"]');
    const consent = form.querySelector('input[name="consent"]');
    const button = form.querySelector('button[type="submit"]');
    const error = form.querySelector('[data-sb-klaviyo-error]');
    const success = form.querySelector('[data-sb-klaviyo-success]');
    if (!email || !button || !error || !success) return;
    form.dataset.sbKlaviyoReady = 'true';
    const label = button.textContent;
    const { company, list, loadingLabel, invalidMessage, consentMessage, failMessage } = form.dataset;

    const fail = (message, field) => {
      error.textContent = message;
      error.hidden = false;
      if (field) field.focus();
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      error.hidden = true;
      const value = email.value.trim();
      if (!EMAIL.test(value)) return fail(invalidMessage, email);
      if (consent && !consent.checked) return fail(consentMessage, consent);

      button.disabled = true;
      button.textContent = loadingLabel || label;
      try {
        const response = await subscribe(company, list, value);
        if (!response.ok) {
          let detail = '';
          try {
            const data = await response.json();
            detail = data && data.errors && data.errors[0] && data.errors[0].detail;
          } catch (parseError) {
            detail = '';
          }
          throw new Error(detail || failMessage);
        }
        form.querySelectorAll('[data-sb-klaviyo-fields]').forEach((element) => {
          element.hidden = true;
        });
        success.hidden = false;
      } catch (submitError) {
        fail(submitError.message || failMessage);
      } finally {
        button.disabled = false;
        button.textContent = label;
      }
    });

    button.disabled = false;
  }

  const bindWithin = (scope) => scope.querySelectorAll('form[data-sb-klaviyo]').forEach(bind);
  document.addEventListener('shopify:section:load', (event) => bindWithin(event.target));
  bindWithin(document);
})();
